import React, { useState, useEffect } from 'react';
import { Spinner } from 'js/design_system/Spinner';

export interface AnimatedLoadingStatusProps {
  /**
   * Whether to show the loading animation
   */
  isLoading: boolean;
  /**
   * Array of messages to cycle through while loading
   */
  messages: string[];
  /**
   * Text to display when not loading
   */
  defaultText?: string;
  /**
   * Whether to show the spinner indicator (default: true)
   */
  showSpinner?: boolean;
  /**
   * Size of the spinner in pixels (default: 16)
   */
  spinnerSize?: number;
  /**
   * Time in milliseconds between message changes (default: 2000)
   */
  intervalMs?: number;
  /**
   * Optional CSS class name for styling
   */
  className?: string;
  /**
   * Optional inline styles
   */
  style?: React.CSSProperties;
}

/**
 * Default messages for citation analysis loading states
 */
export const CITATION_ANALYSIS_MESSAGES = [
  'Reading document',
  'Identifying claims',
  'Extracting references',
  'Finding references to back up claims',
  'Checking for missing citations',
  'Scanning reference databases',
  'Weighing citation counts',
  'Linking claims to relevant studies',
  "Reviewing your draft's reference list",
  'Highlighting key authors in the field',
  'Mining scholarly archives',
  'Compiling supporting evidence',
  'Detecting duplicate references',
  'Preparing export formats',
  'Curating potential matches',
  'Organizing suggested references',
  'Surfacing high-impact studies',
];

/**
 * A component that cycles through loading messages to provide engaging feedback
 * during long-running operations.
 *
 * @example
 * ```tsx
 * <AnimatedLoadingStatus
 *   isLoading={isAnalyzing}
 *   messages={CITATION_ANALYSIS_MESSAGES}
 *   defaultText="Analysis complete"
 * />
 * ```
 */
export const AnimatedLoadingStatus: React.FC<AnimatedLoadingStatusProps> = ({
  isLoading,
  messages,
  defaultText = '',
  showSpinner = true,
  spinnerSize = 16,
  intervalMs = 2000,
  className = '',
  style,
}) => {
  const [currentMessage, setCurrentMessage] = useState(messages[0] || '');

  useEffect(() => {
    if (!isLoading || messages.length === 0) {
      return;
    }

    // Choose a random starting message
    const randomIndex = Math.floor(Math.random() * messages.length);
    setCurrentMessage(messages[randomIndex]);

    let messageIndex = randomIndex;

    // Cycle through messages at the specified interval
    const interval = setInterval(() => {
      messageIndex = (messageIndex + 1) % messages.length;
      setCurrentMessage(messages[messageIndex]);
    }, intervalMs);

    return () => clearInterval(interval);
  }, [isLoading, messages, intervalMs]);

  if (!isLoading) {
    if (!defaultText) return null;
    return (
      <span className={className} style={style}>
        {defaultText}
      </span>
    );
  }

  return (
    <div
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        ...style,
      }}
    >
      {showSpinner && <Spinner size={spinnerSize} />}
      <span
        style={{
          transition: 'opacity 0.3s ease-in-out',
          color: 'var(--text-tertiary, #718096)',
          minWidth: '240px',
          display: 'inline-block',
        }}
        key={currentMessage}
      >
        {currentMessage}...
      </span>
    </div>
  );
};
